import React, { useState } from 'react';
import { X, Plus, Calendar, Clock, Globe, Save } from 'lucide-react';

const BatchScheduleConfig = ({ schedules, onClose, onSave }) => {
  const [localSchedules, setLocalSchedules] = useState(schedules || []);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newSchedule, setNewSchedule] = useState({
    name: '',
    schedule: '0 0 * * *',
    timezone: 'UTC',
    status: 'active'
  });

  const timezones = [
    'UTC',
    'America/New_York',
    'America/Los_Angeles',
    'Europe/London',
    'Europe/Paris',
    'Asia/Tokyo',
    'Asia/Singapore',
    'Australia/Sydney'
  ];

  const cronPresets = [
    { label: 'Every Hour', value: '0 * * * *' },
    { label: 'Every 6 Hours', value: '0 */6 * * *' },
    { label: 'Daily at Midnight', value: '0 0 * * *' },
    { label: 'Daily at 2 AM', value: '0 2 * * *' },
    { label: 'Weekly (Sunday)', value: '0 0 * * 0' },
    { label: 'Monthly (1st)', value: '0 0 1 * *' }
  ];

  const handleAddSchedule = () => {
    if (newSchedule?.name && newSchedule?.schedule) {
      const schedule = {
        ...newSchedule,
        id: Date.now(),
        lastRun: new Date()?.toISOString(),
        nextRun: new Date(Date.now() + 3600000)?.toISOString(),
        duration: '0m 0s',
        successRate: 100
      };
      setLocalSchedules([...localSchedules, schedule]);
      setNewSchedule({ name: '', schedule: '0 0 * * *', timezone: 'UTC', status: 'active' });
      setShowAddForm(false);
    }
  };

  const handleDeleteSchedule = (scheduleId) => {
    setLocalSchedules(localSchedules?.filter(s => s?.id !== scheduleId));
  };

  const handleSave = () => {
    onSave?.(localSchedules);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Calendar className="w-6 h-6 text-blue-600" />
              Batch Schedule Configuration
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Configure automated batch processing schedules with cron expressions
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-6 h-6 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Add New Schedule Button */}
          {!showAddForm && (
            <button
              onClick={() => setShowAddForm(true)}
              className="w-full mb-4 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors flex items-center justify-center gap-2 text-gray-600 hover:text-blue-600"
            >
              <Plus className="w-5 h-5" />
              Add New Schedule
            </button>
          )}

          {/* Add Schedule Form */}
          {showAddForm && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <h3 className="font-semibold text-gray-900 mb-3">New Batch Schedule</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Schedule Name
                  </label>
                  <input
                    type="text"
                    value={newSchedule?.name}
                    onChange={(e) => setNewSchedule({ ...newSchedule, name: e?.target?.value })}
                    placeholder="e.g., Daily Settlement Reconciliation"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <Clock className="w-4 h-4 inline mr-1" />
                      Cron Expression
                    </label>
                    <select
                      value={newSchedule?.schedule}
                      onChange={(e) => setNewSchedule({ ...newSchedule, schedule: e?.target?.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      {cronPresets?.map((preset) => (
                        <option key={preset?.value} value={preset?.value}>
                          {preset?.label} ({preset?.value})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <Globe className="w-4 h-4 inline mr-1" />
                      Timezone
                    </label>
                    <select
                      value={newSchedule?.timezone}
                      onChange={(e) => setNewSchedule({ ...newSchedule, timezone: e?.target?.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      {timezones?.map((tz) => (
                        <option key={tz} value={tz}>{tz}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={handleAddSchedule}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Add Schedule
                  </button>
                  <button
                    onClick={() => setShowAddForm(false)}
                    className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Existing Schedules */}
          <div className="space-y-3">
            {localSchedules?.map((schedule) => (
              <div
                key={schedule?.id}
                className="bg-white border border-gray-200 rounded-lg p-4"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 mb-2">{schedule?.name}</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-gray-500">Schedule:</span>
                        <span className="ml-2 font-mono bg-gray-100 px-2 py-1 rounded">
                          {schedule?.schedule}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">Timezone:</span>
                        <span className="ml-2 font-medium">{schedule?.timezone}</span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDeleteSchedule(schedule?.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {localSchedules?.length === 0 && !showAddForm && (
            <div className="text-center py-12 text-gray-500">
              <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No schedules configured</p>
              <p className="text-sm">Click "Add New Schedule" to create one</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            Save Configuration
          </button>
        </div>
      </div>
    </div>
  );
};

export default BatchScheduleConfig;